import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './SpecialIssues.css';

const SpecialIssues = () => {
  const [specialIssues, setSpecialIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSpecialIssues();
  }, []);

  const fetchSpecialIssues = async () => {
    try {
      // Simulated API call - replace with actual endpoint
      const response = await axios.get('/api/special-issues');
      // Ensure response.data is an array
      if (Array.isArray(response.data)) {
        setSpecialIssues(response.data);
      } else {
        throw new Error('Invalid data format');
      }
    } catch (err) {
      console.error('Error fetching special issues:', err);
      // Use placeholder data if API fails
      setSpecialIssues([
        {
          id: 1,
          title: "Climate Change Adaptation in West African Agriculture",
          status: "open",
          deadline: "2024-12-31",
          guestEditors: ["Dr. Amina Hassan", "Prof. Emmanuel Okoro"],
          description: "This special issue focuses on innovative strategies for climate change adaptation in West African agricultural systems.",
          expectedPublication: "March 2025",
          submissionCount: 12,
          maxArticles: 15
        },
        {
          id: 2,
          title: "Sustainable Livestock Production Systems",
          status: "open",
          deadline: "2025-01-15",
          guestEditors: ["Prof. Sarah Yakubu", "Dr. Bartholomew Mohammed"],
          description: "Exploring sustainable approaches to livestock production with emphasis on environmental conservation and economic viability.",
          expectedPublication: "June 2025",
          submissionCount: 8,
          maxArticles: 12
        },
        {
          id: 3,
          title: "Digital Agriculture and Smart Farming Technologies",
          status: "planning",
          deadline: "2025-03-30",
          guestEditors: ["Dr. Rasheed Aliyu", "Prof. Fatima Mohammed"],
          description: "Integration of digital technologies, IoT, and precision agriculture in developing country contexts.",
          expectedPublication: "September 2025",
          submissionCount: 0,
          maxArticles: 20
        },
        {
          id: 4,
          title: "Food Security and Nutrition in Sub-Saharan Africa",
          status: "closed",
          deadline: "2024-08-31",
          guestEditors: ["Prof. Abdullahi Usman", "Dr. Priscilla Nwosu"],
          description: "Comprehensive analysis of food security challenges and innovative solutions across Sub-Saharan Africa.",
          expectedPublication: "December 2024",
          submissionCount: 18,
          maxArticles: 18
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <span className="badge bg-success">Open for Submissions</span>;
      case 'closed':
        return <span className="badge bg-danger">Closed</span>;
      case 'planning':
        return <span className="badge bg-warning text-dark">Coming Soon</span>;
      case 'review':
        return <span className="badge bg-info">Under Review</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="row mb-5">
        <div className="col-lg-10 mx-auto">
          <h1 className="mb-4 text-center">Special Issues</h1>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">About Special Issues</h2>
              <p className="card-text">
                FUWJALS regularly publishes special issues focusing on specific themes, emerging 
                topics, or significant challenges in agriculture and life sciences. These curated 
                collections bring together high-quality research from leading experts and provide 
                comprehensive coverage of important subjects.
              </p>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Current and Upcoming Special Issues</h2>
              
              {specialIssues.map((issue) => (
                <div key={issue.id} className="card mb-4 border-start border-primary border-4">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-8">
                        <h4 className="card-title">{issue.title}</h4>
                        <p className="card-text">{issue.description}</p>
                        
                        <div className="row mb-3">
                          <div className="col-sm-6">
                            <small className="text-muted">
                              <strong>Submission Deadline:</strong> {issue.deadline}
                            </small>
                          </div>
                          <div className="col-sm-6">
                            <small className="text-muted">
                              <strong>Expected Publication:</strong> {issue.expectedPublication}
                            </small>
                          </div>
                        </div>
                        
                        <div className="mb-2">
                          <strong>Guest Editors:</strong> {issue.guestEditors.join(', ')}
                        </div>
                      </div>
                      
                      <div className="col-md-4 text-end">
                        <div className="mb-3">
                          {getStatusBadge(issue.status)}
                        </div>
                        
                        {issue.status === 'open' && (
                          <div className="mb-3">
                            <div className="progress">
                              <div 
                                className="progress-bar bg-success" 
                                role="progressbar" 
                                style={{ width: `${(issue.submissionCount / issue.maxArticles) * 100}%` }}
                              >
                                {issue.submissionCount}/{issue.maxArticles}
                              </div>
                            </div>
                            <small className="text-muted">Articles submitted</small>
                          </div>
                        )}
                        
                        {issue.status === 'open' && (
                          <Link 
                            to={`/submit-paper?special_issue=${issue.id}`} 
                            className="btn btn-primary btn-sm"
                          >
                            Submit Article
                          </Link>
                        )}
                        
                        {issue.status === 'closed' && (
                          <button className="btn btn-secondary btn-sm" disabled>
                            Submissions Closed
                          </button>
                        )}
                        
                        {issue.status === 'planning' && (
                          <button className="btn btn-outline-primary btn-sm" disabled>
                            Coming Soon
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Benefits of Special Issue Publications</h2>
              <div className="row">
                <div className="col-md-6">
                  <h4>For Authors</h4>
                  <ul>
                    <li><strong>Increased Visibility:</strong> Thematic collections attract more readers</li>
                    <li><strong>Expert Networking:</strong> Connect with leading researchers in your field</li>
                    <li><strong>Faster Review:</strong> Dedicated guest editors ensure timely processing</li>
                    <li><strong>Higher Citations:</strong> Special issues typically receive more citations</li>
                    <li><strong>Professional Recognition:</strong> Association with prestigious collections</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>For Readers</h4>
                  <ul>
                    <li><strong>Comprehensive Coverage:</strong> Complete overview of specific topics</li>
                    <li><strong>Quality Curation:</strong> Expert-selected high-quality research</li>
                    <li><strong>Current Relevance:</strong> Focus on timely and important issues</li>
                    <li><strong>Diverse Perspectives:</strong> Multiple viewpoints on key challenges</li>
                    <li><strong>Practical Applications:</strong> Real-world solutions and case studies</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Special Issue Submission Guidelines</h2>
              <div className="alert alert-info">
                <h4><i className="fas fa-info-circle me-2"></i>Important Notice</h4>
                <p className="mb-0">
                  All special issue submissions must follow standard FUWJALS submission guidelines 
                  with additional requirements specific to each special issue theme.
                </p>
              </div>
              
              <h4>Additional Requirements</h4>
              <ul>
                <li><strong>Relevance Statement:</strong> Explain how your research fits the special issue theme</li>
                <li><strong>Extended Abstract:</strong> Provide a detailed abstract (up to 400 words)</li>
                <li><strong>Keywords Alignment:</strong> Include special issue keywords in your submission</li>
                <li><strong>Literature Context:</strong> Position your work within the thematic framework</li>
                <li><strong>Impact Statement:</strong> Describe potential impact on the focus area</li>
              </ul>
              
              <h4>Review Process</h4>
              <div className="row">
                <div className="col-md-6">
                  <h5>Guest Editor Review</h5>
                  <ul>
                    <li>Initial thematic relevance assessment</li>
                    <li>Quality and significance evaluation</li>
                    <li>Recommendation to Editor-in-Chief</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Peer Review</h5>
                  <ul>
                    <li>Standard double-blind process</li>
                    <li>Specialized reviewers with thematic expertise</li>
                    <li>Expedited timeline when possible</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Past Special Issues</h2>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="card bg-light h-100">
                    <div className="card-body">
                      <h5 className="card-title">Sustainable Agriculture Practices (2024)</h5>
                      <p className="card-text small">
                        Volume 8, Issue 2 - Published June 2024<br />
                        15 articles, 2,500+ downloads<br />
                        Guest Editors: Prof. A. Usman, Dr. F. Mohammed
                      </p>
                      <Link to="/issues/8/2" className="btn btn-sm btn-outline-primary">View Issue</Link>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6 mb-3">
                  <div className="card bg-light h-100">
                    <div className="card-body">
                      <h5 className="card-title">Post-Harvest Technologies (2023)</h5>
                      <p className="card-text small">
                        Volume 7, Issue 4 - Published December 2023<br />
                        12 articles, 3,200+ downloads<br />
                        Guest Editors: Dr. R. Aliyu, Prof. S. Yakubu
                      </p>
                      <Link to="/issues/7/4" className="btn btn-sm btn-outline-primary">View Issue</Link>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6 mb-3">
                  <div className="card bg-light h-100">
                    <div className="card-body">
                      <h5 className="card-title">Aquaculture Innovations (2023)</h5>
                      <p className="card-text small">
                        Volume 7, Issue 3 - Published September 2023<br />
                        10 articles, 1,800+ downloads<br />
                        Guest Editors: Prof. E. Okoro, Dr. P. Nwosu
                      </p>
                      <Link to="/issues/7/3" className="btn btn-sm btn-outline-primary">View Issue</Link>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6 mb-3">
                  <div className="card bg-light h-100">
                    <div className="card-body">
                      <h5 className="card-title">Agroforestry Systems (2023)</h5>
                      <p className="card-text small">
                        Volume 7, Issue 1 - Published March 2023<br />
                        14 articles, 2,800+ downloads<br />
                        Guest Editors: Dr. A. Hassan, Prof. B. Mohammed
                      </p>
                      <Link to="/issues/7/1" className="btn btn-sm btn-outline-primary">View Issue</Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Proposing a Special Issue</h2>
              <p className="card-text">
                FUWJALS welcomes proposals for special issues from recognized experts in agriculture 
                and life sciences. We encourage topics that address current challenges, emerging 
                technologies, or underexplored areas in our field.
              </p>
              
              <h4>Proposal Requirements</h4>
              <div className="row">
                <div className="col-md-6">
                  <h5>Content Requirements</h5>
                  <ul>
                    <li>Clear thematic focus and rationale</li>
                    <li>Comprehensive topic coverage plan</li>
                    <li>Target audience identification</li>
                    <li>Timeline and milestone schedule</li>
                    <li>Marketing and promotion strategy</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Editorial Team</h5>
                  <ul>
                    <li>2-3 qualified guest editors</li>
                    <li>Demonstrated expertise in theme area</li>
                    <li>Strong publication track record</li>
                    <li>International network of contacts</li>
                    <li>Commitment to editorial timeline</li>
                  </ul>
                </div>
              </div>
              
              <h4>Proposal Evaluation</h4>
              <ul>
                <li><strong>Scientific Merit:</strong> Significance and timeliness of the topic</li>
                <li><strong>Editorial Quality:</strong> Qualifications of proposed guest editors</li>
                <li><strong>Market Demand:</strong> Potential reader and author interest</li>
                <li><strong>Journal Fit:</strong> Alignment with FUWJALS scope and mission</li>
                <li><strong>Resource Requirements:</strong> Feasibility within journal capacity</li>
              </ul>
              
              <div className="text-center mt-4">
                <a href="mailto:special-issues@fuwukari.edu.ng" className="btn btn-primary">
                  <i className="fas fa-lightbulb me-2"></i>Submit Special Issue Proposal
                </a>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Guest Editor Responsibilities</h2>
              <div className="row">
                <div className="col-md-6">
                  <h4>Pre-Launch Phase</h4>
                  <ul>
                    <li>Develop call for papers</li>
                    <li>Promote special issue widely</li>
                    <li>Recruit potential authors</li>
                    <li>Establish reviewer network</li>
                    <li>Set realistic timeline</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>Active Phase</h4>
                  <ul>
                    <li>Initial manuscript screening</li>
                    <li>Reviewer selection and invitation</li>
                    <li>Review process coordination</li>
                    <li>Editorial decision recommendations</li>
                    <li>Author communication</li>
                  </ul>
                </div>
              </div>
              
              <h4>Benefits for Guest Editors</h4>
              <ul>
                <li>Professional recognition and visibility</li>
                <li>Networking with international experts</li>
                <li>Editorial experience and skills development</li>
                <li>Contribution to field advancement</li>
                <li>Certificate of editorial service</li>
                <li>Consideration for FUWJALS Editorial Board</li>
              </ul>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Frequently Asked Questions</h2>
              
              <h5>Q: Can I submit to a special issue and regular issue simultaneously?</h5>
              <p>A: No, manuscripts can only be submitted to one outlet at a time. Choose the most appropriate option for your research.</p>
              
              <h5>Q: Are special issue publication fees different?</h5>
              <p>A: Special issues follow the same fee structure as regular issues, with the same discount and waiver programs available.</p>
              
              <h5>Q: What happens if my manuscript doesn't fit the special issue theme?</h5>
              <p>A: If deemed unsuitable for the special issue, it may be considered for the regular journal with your permission.</p>
              
              <h5>Q: Can I suggest reviewers for my special issue submission?</h5>
              <p>A: Yes, you may suggest reviewers, but the final selection remains with the guest editors and editorial team.</p>
              
              <h5>Q: How are special issue articles promoted?</h5>
              <p>A: Special issues receive enhanced promotion through social media, email newsletters, and academic conferences.</p>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h2 className="card-title">Contact Information</h2>
              <div className="row">
                <div className="col-md-6">
                  <h5>Special Issues Coordination</h5>
                  <p>
                    <i className="fas fa-envelope me-2"></i>special-issues@fuwukari.edu.ng<br />
                    <i className="fas fa-phone me-2"></i>+234 XXX XXX XXXX<br />
                    <i className="fas fa-clock me-2"></i>Mon-Fri, 9 AM - 5 PM WAT
                  </p>
                </div>
                <div className="col-md-6">
                  <h5>Guest Editor Inquiries</h5>
                  <p>
                    <i className="fas fa-envelope me-2"></i>guest-editors@fuwukari.edu.ng<br />
                    <i className="fas fa-question-circle me-2"></i>Editorial guidance<br />
                    <i className="fas fa-users me-2"></i>Collaboration opportunities
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/submit-paper" className="btn btn-primary me-3">Submit to Special Issue</Link>
            <a href="mailto:special-issues@fuwukari.edu.ng" className="btn btn-outline-secondary">Propose New Issue</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecialIssues;